import { yupResolver } from "@hookform/resolvers/yup";
import FormButtons, { FormButtonInterface } from "components/forms/buttons";
import { SubmitButton } from "components/forms/buttons.utils";
import Form from "components/forms/form";
import Dropdown from "components/forms/inputs/dropdown";
import { OptionInterface } from "components/forms/inputs/dropdown.styles";
import { getLanguageOptions } from "components/forms/inputs/dropdown.utils";
import TextInput from "components/forms/inputs/text";
import TextareaInput from "components/forms/inputs/textarea";
import Col from "components/grid/col";
import Grid from "components/grid/grid";
import Row from "components/grid/row";
import { faSave } from "components/icon-library";
import Info from "components/info";
import { showError, showSuccess } from "lib/notification";
import { Admin } from "models/admin";
import { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { adminDoPatchProfile } from "services/admin/profile";
import { fetchAuthAdmin } from "stores/actions/auth";
import { useLocales } from "stores/hooks/locale";
import { useTranslate } from "stores/hooks/translation";
import * as yup from "yup";

interface BaseFormProps {
  admin: Admin;
}

const BaseForm: FunctionComponent<BaseFormProps> = ({ admin }) => {
  const locales = useLocales();
  const translate = useTranslate();

  const schema = yup
    .object({
      firstname: yup.string().validateRequired(),
      lastname: yup.string().validateRequired(),
      email: yup.string().validateEmail().validateRequired(),
      phone: yup.string().validatePhone(),
      locale: yup.string().validateOneOf(locales).validateRequired(),
      ssn: yup.string().validateSsn(),
      signature: yup.string(),
    })
    .required();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: admin.firstname || "",
      lastname: admin.lastname || "",
      phone: admin.phone || "",
      email: admin.email,
      locale: admin.locale,
      ssn: admin.ssn || "",
      signature: admin.signature || "",
    },
  });

  const onSubmit = async (values: any) => {
    try {
      const { firstname, lastname, phone, locale, ssn, signature } = values;

      await adminDoPatchProfile({
        firstname,
        lastname,
        phone,
        locale,
        ssn,
        signature,
      });

      await fetchAuthAdmin();

      showSuccess("Tietojen päivitys onnistui.");
    } catch (err) {
      showError("Tietojen päivitys epäonnistui.");
    }
  };

  const buttons: FormButtonInterface[] = [
    { ...SubmitButton, align: "left", postIcon: faSave },
  ];

  const localeOptions: OptionInterface[] = getLanguageOptions(
    locales,
    translate,
  );

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Grid>
        <Row>
          <Col>
            <TextInput name="firstname" label="Etunimi" required />
          </Col>
          <Col>
            <TextInput name="lastname" label="Sukunimi" required />
          </Col>
        </Row>
        <Row>
          <Col>
            <Dropdown
              name="locale"
              label="Kieli"
              options={localeOptions}
              required
            />
          </Col>
          <Col>
            <TextInput name="phone" label="Puhelin" />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInput
              disabled
              name="email"
              label="Sähköpostiosoite"
              required
            />
          </Col>
          <Col>
            <TextInput name="ssn" label="Henkilötunnus" />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextareaInput
              name="signature"
              label="Sähköpostin allekirjoitus"
              height={10}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Info>
              Henkilötunnusta tarvitaan vahvassa tunnistatumisessa.
              Kirjauduttaessa sähköpostiosoitteella ja salasanalla henkilötunnus
              ei ole pakollinen. Henkilötunnuksen puuttuessa vahva
              tunnistautuminen epäonnistuu.
            </Info>
          </Col>
        </Row>
        <FormButtons buttons={buttons} />
      </Grid>
    </Form>
  );
};

export default BaseForm;
